/**
 * Workflow Template Selection Modal
 * Allows users to choose from predefined workflow templates when creating new workflows
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBootstrapTheme } from '../../contexts/BootstrapThemeContext';

export interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  preview: string;
  difficulty: 'Beginner' | 'Intermediate' | 'Advanced';
  estimatedTime: string;
  features: string[];
  nodeCount: number;
  tags: string[];
}

const workflowTemplates: WorkflowTemplate[] = [
  {
    id: 'empty',
    name: 'Empty Canvas',
    description: 'Start with a blank canvas and build your workflow from scratch',
    category: 'Basic',
    icon: '📄',
    preview: '/templates/preview-empty.png',
    difficulty: 'Beginner',
    estimatedTime: '5-10 min',
    features: ['Clean slate', 'Full flexibility', 'Custom design'],
    nodeCount: 0,
    tags: ['starter', 'custom', 'blank']
  },
  {
    id: 'chat-assistant',
    name: 'Chat Assistant',
    description: 'Pre-configured chat workflow with AI integration, memory, and response formatting',
    category: 'AI & Chat',
    icon: '💬',
    preview: '/templates/preview-chat.png',
    difficulty: 'Beginner',
    estimatedTime: '2-5 min',
    features: ['AI Chat Integration', 'Message History', 'Response Templates', 'Context Memory'],
    nodeCount: 8,
    tags: ['ai', 'chat', 'assistant', 'conversation']
  },
  {
    id: 'image-generator',
    name: 'Image Generator',
    description: 'Complete image generation setup with prompt engineering, style controls, and output management',
    category: 'Creative',
    icon: '🎨',
    preview: '/templates/preview-image-gen.png',
    difficulty: 'Intermediate',
    estimatedTime: '5-15 min',
    features: ['Stable Diffusion', 'Prompt Enhancement', 'Style Presets', 'Batch Generation'],
    nodeCount: 12,
    tags: ['image', 'ai', 'art', 'generation', 'creative']
  },
  {
    id: 'data-processor',
    name: 'Data Processing Pipeline',
    description: 'Automated data processing with validation, transformation, and export capabilities',
    category: 'Data',
    icon: '📊',
    preview: '/templates/preview-data.png',
    difficulty: 'Intermediate',
    estimatedTime: '10-20 min',
    features: ['CSV/JSON Input', 'Data Validation', 'Transformation Rules', 'Export Options'],
    nodeCount: 15,
    tags: ['data', 'processing', 'automation', 'csv', 'json']
  },
  {
    id: 'content-creator',
    name: 'Content Creation Suite',
    description: 'End-to-end content creation with research, writing, editing, and SEO optimization',
    category: 'Content',
    icon: '✍️',
    preview: '/templates/preview-content.png',
    difficulty: 'Advanced',
    estimatedTime: '15-30 min',
    features: ['Research Automation', 'AI Writing', 'SEO Optimization', 'Multi-Format Export'],
    nodeCount: 20,
    tags: ['content', 'writing', 'seo', 'marketing', 'blog']
  },
  {
    id: 'api-integration',
    name: 'API Integration Hub',
    description: 'Connect and orchestrate multiple APIs with error handling and data mapping',
    category: 'Integration',
    icon: '🔌',
    preview: '/templates/preview-api.png',
    difficulty: 'Advanced',
    estimatedTime: '20-40 min',
    features: ['Multi-API Support', 'Error Handling', 'Data Mapping', 'Response Caching'],
    nodeCount: 18,
    tags: ['api', 'integration', 'automation', 'webhook', 'data']
  }
];

interface WorkflowTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate?: (template: WorkflowTemplate) => void;
}

export const WorkflowTemplateModal= ({
  isOpen,
  onClose,
  onSelectTemplate
}: WorkflowTemplateModalProps) => {
  const { effectiveTheme } = useBootstrapTheme();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);

  const categories = ['All', 'Basic', 'AI & Chat', 'Creative', 'Data', 'Content', 'Integration'];

  const filteredTemplates = workflowTemplates.filter(template => 
    selectedCategory === 'All' || template.category === selectedCategory
  );

  const handleTemplateSelect = (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
  };

  const handleCreateWorkflow = () => {
    if (selectedTemplate) {
      if (onSelectTemplate) {
        onSelectTemplate(selectedTemplate);
      } else {
        // Navigate to workflow editor with template parameter
        navigate(`/workflows/new?template=${selectedTemplate.id}`);
      }
      onClose();
    }
  };

  const getDifficultyBadgeClass = (difficulty: string) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-success';
      case 'Intermediate': return 'bg-warning';
      case 'Advanced': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className={`modal-content ${effectiveTheme === 'dark' ? 'bg-dark text-white' : ''}`}>
          {/* Header */}
          <div className="modal-header">
            <div>
              <h4 className="modal-title mb-1">🚀 Choose Workflow Template</h4>
              <p className="text-muted mb-0">Start with a pre-built template to get up and running quickly</p>
            </div>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>

          {/* Body */}
          <div className="modal-body">
            <div className="row">
              {/* Categories Sidebar */}
              <div className="col-md-3">
                <div className="card">
                  <div className="card-header">
                    <h6 className="card-title mb-0">📂 Categories</h6>
                  </div>
                  <div className="card-body p-0">
                    <div className="list-group list-group-flush">
                      {categories.map(category => (
                        <button
                          key={category}
                          className={`list-group-item list-group-item-action ${
                            selectedCategory === category ? 'active' : ''
                          }`}
                          onClick={() => setSelectedCategory(category)}
                        >
                          {category}
                          <span className="badge bg-secondary ms-auto">
                            {category === 'All' 
                              ? workflowTemplates.length 
                              : workflowTemplates.filter(t => t.category === category).length
                            }
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Templates Grid */}
              <div className="col-md-9">
                <div className="row g-3">
                  {filteredTemplates.map(template => (
                    <div key={template.id} className="col-sm-6 col-lg-4">
                      <div 
                        className={`card h-100 ${
                          selectedTemplate?.id === template.id ? 'border-primary shadow-sm' : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleTemplateSelect(template)}
                      >
                        {/* Template Preview */}
                        <div className="card-img-top bg-light d-flex align-items-center justify-content-center" 
                             style={{ height: '120px', fontSize: '3rem' }}>
                          {template.icon}
                        </div>

                        <div className="card-body p-3">
                          {/* Header */}
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0 fw-bold">{template.name}</h6>
                            <span className={`badge ${getDifficultyBadgeClass(template.difficulty)}`}>
                              {template.difficulty}
                            </span>
                          </div>

                          {/* Description */}
                          <p className="card-text small text-muted mb-3">
                            {template.description}
                          </p>

                          {/* Stats */}
                          <div className="d-flex justify-content-between align-items-center mb-2 small">
                            <span className="text-muted">
                              ⏱️ {template.estimatedTime}
                            </span>
                            <span className="text-muted">
                              🔗 {template.nodeCount} nodes
                            </span>
                          </div>

                          {/* Features */}
                          <div className="mb-3">
                            {template.features.slice(0, 2).map(feature => (
                              <span key={feature} className="badge bg-light text-dark me-1 mb-1 small">
                                {feature}
                              </span>
                            ))}
                            {template.features.length > 2 && (
                              <span className="small text-muted">
                                +{template.features.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Selection Indicator */}
                        {selectedTemplate?.id === template.id && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <div className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                 style={{ width: '24px', height: '24px' }}>
                              ✓
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {filteredTemplates.length === 0 && (
                  <div className="text-center py-5">
                    <div className="mb-3" style={{ fontSize: '3rem' }}>📋</div>
                    <h5>No templates found</h5>
                    <p className="text-muted">Try selecting a different category</p>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Template Details */}
            {selectedTemplate && (
              <div className="row mt-4">
                <div className="col-12">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white">
                      <h6 className="mb-0">
                        {selectedTemplate.icon} {selectedTemplate.name} - Template Details
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-8">
                          <h6>✨ Features</h6>
                          <div className="row g-2 mb-3">
                            {selectedTemplate.features.map(feature => (
                              <div key={feature} className="col-sm-6">
                                <span className="badge bg-light text-dark w-100 text-start">
                                  ✓ {feature}
                                </span>
                              </div>
                            ))}
                          </div>

                          <h6>🏷️ Tags</h6>
                          <div className="mb-3">
                            {selectedTemplate.tags.map(tag => (
                              <span key={tag} className="badge bg-secondary me-1 mb-1">
                                #{tag}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="col-md-4">
                          <h6>📊 Template Info</h6>
                          <ul className="list-unstyled">
                            <li className="mb-2">
                              <strong>Category:</strong> {selectedTemplate.category}
                            </li>
                            <li className="mb-2">
                              <strong>Difficulty:</strong> 
                              <span className={`badge ${getDifficultyBadgeClass(selectedTemplate.difficulty)} ms-2`}>
                                {selectedTemplate.difficulty}
                              </span>
                            </li>
                            <li className="mb-2">
                              <strong>Setup Time:</strong> {selectedTemplate.estimatedTime}
                            </li>
                            <li className="mb-2">
                              <strong>Node Count:</strong> {selectedTemplate.nodeCount} nodes
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              disabled={!selectedTemplate}
              onClick={handleCreateWorkflow}
            >
              <span className="me-2">🚀</span>
              Create Workflow
              {selectedTemplate && (
                <span className="ms-2 small opacity-75">
                  ({selectedTemplate.name})
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};